import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';
import { getProductSizeRange } from './functions';
import { formatPrice } from '../Components/Orders/pdf/utils/priceFormatter';

const CartCard = ({ group, onDelete, readOnly = false }) => {
    const navigate = useNavigate();
    const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);

    const {
        sku,
        styleFactory,
        color,
        thumbnail,
        Price1,
        Price2,
        Price3,
        Size1,
        Size2,
        Size3,
        items
    } = group;

    const sizeRange = React.useMemo(() => {
        return getProductSizeRange(group);
    }, [group]);

    // Create a map of size -> item for fast lookup
    const sizeMap = React.useMemo(() => {
        const map = {};
        items.forEach(item => {
            map[item.size] = item;
        });
        return map;
    }, [items]);

    // Calculate total pairs for this group
    const totalPairs = React.useMemo(() => {
        return items.reduce((sum, item) => sum + item.unitQuantity, 0);
    }, [items]);

    // Calculate total price for this group
    const totalPrice = React.useMemo(() => {
        return items.reduce((sum, item) => sum + (item.unitQuantity * item.unitFinalPrice), 0);
    }, [items]);

    const handleEdit = () => {
        navigate('/SelectVariation');
    };

    const handleDeleteClick = () => {
        setOpenDeleteConfirm(true);
    };

    const handleConfirmDelete = () => {
        onDelete(sku);
        setOpenDeleteConfirm(false);
    };

    return (
        <Card
            sx={{
                width: '100%',
                mb: 3,
                borderRadius: 2,
                border: '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                position: 'relative',
                overflow: 'visible'
            }}
        >
            <CardContent sx={{ p: 3 }}>
                <Grid container spacing={3} alignItems="center">
                    {/* Thumbnail & Product Details */}
                    <Grid item xs={12} md={3} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Box
                            component="img"
                            src={thumbnail || '/blank-image.png'}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "/blank-image.png";
                            }}
                            alt={styleFactory}
                            sx={{
                                width: 90,
                                height: 90,
                                objectFit: 'contain',
                                borderRadius: 1,
                                backgroundColor: '#f8f9fa',
                                border: '1px solid rgba(0, 0, 0, 0.05)',
                                p: 0.5
                            }}
                        />
                        <Box>
                            <Typography variant="subtitle1" fontWeight="700" color="text.primary" sx={{ fontSize: '1.05rem' }}>
                                {styleFactory}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" fontWeight="500" sx={{ mt: 0.5, fontSize: '0.85rem' }}>
                                SKU: {sku}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.85rem' }}>
                                Color: {color}
                            </Typography>

                            {!readOnly && (
                                <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                                    <IconButton size="small" color="primary" onClick={handleEdit} sx={{ border: '1px solid', borderColor: 'primary.light', p: 0.5 }}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" color="error" onClick={handleDeleteClick} sx={{ border: '1px solid', borderColor: 'error.light', p: 0.5 }}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            )}
                        </Box>
                    </Grid>

                    {/* Scrollable Size Grid Table */}
                    <Grid item xs={12} md={9}>
                        <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                                border: '1px solid rgba(0,0,0,0.06)',
                                overflowX: 'auto',
                                '&::-webkit-scrollbar': { height: '6px' },
                                '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: '3px' }
                            }}
                        >
                            <Table size="small" sx={{ minWidth: Math.max(300, sizeRange.length * 35) }}>
                                <TableHead sx={{ backgroundColor: '#fdfdfd', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                                    <TableRow>
                                        {sizeRange.map(sz => (
                                            <TableCell
                                                key={sz}
                                                align="center"
                                                sx={{
                                                    fontWeight: '700',
                                                    fontSize: '0.85rem',
                                                    p: 1,
                                                    borderRight: '1px solid rgba(0,0,0,0.05)',
                                                    backgroundColor: sizeMap[sz]?.unitQuantity > 0 ? 'rgba(25,118,210,0.05)' : 'inherit'
                                                }}
                                            >
                                                {sz}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        {sizeRange.map(sz => {
                                            const qty = sizeMap[sz]?.unitQuantity || 0;
                                            return (
                                                <TableCell
                                                    key={sz}
                                                    align="center"
                                                    sx={{
                                                        fontWeight: qty > 0 ? 'bold' : 'normal',
                                                        color: qty > 0 ? 'primary.main' : 'text.disabled',
                                                        fontSize: '0.95rem',
                                                        p: 1,
                                                        borderRight: '1px solid rgba(0,0,0,0.05)',
                                                        backgroundColor: qty > 0 ? 'rgba(25,118,210,0.02)' : 'inherit'
                                                    }}
                                                >
                                                    {qty > 0 ? qty : ''}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Price bands & subtotal info */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5, flexWrap: 'wrap', gap: 2 }}>
                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                {Size1 && Price1 && (
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                        {Size1} = <span style={{ color: '#1976d2' }}>${Price1}</span>
                                    </Typography>
                                )}
                                {Size2 && Price2 && (
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                        {Size2} = <span style={{ color: '#1976d2' }}>${Price2}</span>
                                    </Typography>
                                )}
                                {Size3 && Price3 && (
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                        {Size3} = <span style={{ color: '#1976d2' }}>${Price3}</span>
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                <Typography variant="subtitle1" fontWeight="700" color="text.secondary">
                                    {totalPairs} Pairs
                                </Typography>
                                <Typography variant="h6" fontWeight="800" color="primary.main">
                                    Total = ${formatPrice(totalPrice.toFixed(2))}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteConfirm} onClose={() => setOpenDeleteConfirm(false)}>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Remove Item?</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        Are you sure you want to remove all sizes of product <strong>{styleFactory}</strong> from the cart?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenDeleteConfirm(false)} variant="outlined" color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} variant="contained" color="error">
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

export default CartCard;